// SF4 — Items per Order.
//
// Two definitions of what an order holds, shown side by side because they
// answer different questions. An order of three identical units is three items
// and one distinct product: the first says how much was bought, the second how
// wide the basket was. Reporting only one would hide the difference.
//
// The product rows deliberately do not average out to the headline figures.
// Each answers only for the orders that contained that product, and the column
// labels say so rather than leaving a reader trying to reconcile them.
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { FiAlertTriangle, FiInfo } from 'react-icons/fi';

import AuthContext from '../../../../context/AuthContext';
import local from '../OrderAnalytics.module.css';
import { getItemsPerOrder } from '../services/orderAnalyticsApi';

const MEASURES = [
  { key: 'units', label: 'Units', note: 'How many things were bought' },
  { key: 'products', label: 'Distinct products', note: 'How wide the basket was' },
];

const inr = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 });

const Blank = () => <span className={local.blank}>—</span>;

const count = (value) =>
  value === null || value === undefined ? <Blank /> : inr.format(value);

const percent = (ratio, digits = 1) =>
  ratio === null || ratio === undefined ? <Blank /> : `${(ratio * 100).toFixed(digits)}%`;

const Change = ({ value }) => {
  if (value === null || value === undefined) return <Blank />;
  const up = value > 0;
  return (
    <span className={up ? local.changeUp : local.changeDown}>
      {up ? '+' : ''}{(value * 100).toFixed(1)}%
    </span>
  );
};

const ItemsPerOrder = ({ window: reportingWindow, onLoadingChange }) => {
  const authCtx = useContext(AuthContext);
  const token = authCtx?.token;
  const { start, end } = reportingWindow || {};

  const [measure, setMeasure] = useState('units');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!token || !start || !end) return;
    setLoading(true);
    onLoadingChange?.(true);
    setError(null);
    try {
      setData(await getItemsPerOrder(token, { start, end }));
    } catch (err) {
      setError(err);
      setData(null);
    } finally {
      setLoading(false);
      onLoadingChange?.(false);
    }
  }, [token, start, end, onLoadingChange]);

  useEffect(() => { load(); }, [load]);

  // Unmounting mid-load would otherwise leave the page's Apply button
  // stuck reporting this component as still running.
  useEffect(() => () => onLoadingChange?.(false), [onLoadingChange]);

  const summary = data?.summary;
  const buckets = data?.distribution?.[measure] || [];
  const widest = Math.max(...buckets.map((b) => b.orders || 0), 0);
  const active = MEASURES.find((m) => m.key === measure);

  return (
    <section className={local.card}>
      <div className={local.cardHead}>
        <h2 className={local.cardTitle}>Items per order</h2>
        {summary && <span className={local.muted}>{count(summary.orders)} orders</span>}
      </div>
      <p className={local.muted}>
        How much customers buy at a time. An order of three identical units is
        three items but one product, so both counts are shown.
      </p>

      {error && (
        <div className={`${local.banner} ${error.reportUnavailable ? local.bannerWarn : local.bannerError}`}>
          <FiAlertTriangle className={local.bannerIcon} />
          <div>
            <strong>
              {error.notConnected ? 'No Amazon account connected'
                : error.reportUnavailable ? 'Data not ready' : 'Could not load basket sizes'}
            </strong>
            <div>{error.message}</div>
          </div>
        </div>
      )}

      {!error && loading && !data && <div className={local.emptyCell}>Loading…</div>}

      {!error && summary && (
        <>
          <div className={local.ratios}>
            <div>
              <div className={local.ratioLabel}>Average units per order</div>
              <div className={local.ratioValue}>
                {summary.average_units_per_order?.toFixed(2) ?? '—'}
              </div>
              <div className={local.ratioFoot}>
                <Change value={summary.average_units_change} /> against the prior period
              </div>
            </div>
            <div>
              <div className={local.ratioLabel}>Average products per order</div>
              <div className={local.ratioValue}>
                {summary.average_products_per_order?.toFixed(2) ?? '—'}
              </div>
              <div className={local.ratioFoot}>
                <Change value={summary.average_products_change} /> against the prior period
              </div>
            </div>
            <div>
              <div className={local.ratioLabel}>Orders with more than one product</div>
              <div className={local.ratioValue}>{percent(summary.multi_product_share)}</div>
              <div className={local.ratioFoot}>
                {count(summary.multi_product_orders)} of {count(summary.orders)}
              </div>
            </div>
          </div>

          <div className={local.groupHead}>
            <h3 className={local.groupTitle}>
              Basket size distribution
              <span className={local.groupCount}>{active?.note}</span>
            </h3>
            <div className={local.segmented}>
              {MEASURES.map((option) => (
                <button key={option.key} type="button"
                  className={option.key === measure ? local.segmentOn : local.segment}
                  onClick={() => setMeasure(option.key)}>
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <div className={local.bars}>
            {buckets.map((bucket) => (
              <div key={bucket.size} className={local.barRow}>
                <span className={local.barLabel}>{bucket.size}</span>
                <span className={local.barTrack}>
                  <span className={local.barFill}
                    style={{ width: widest ? `${Math.max((bucket.orders / widest) * 100, 0.5)}%` : '0%' }} />
                </span>
                <span className={local.barValue}>{count(bucket.orders)}</span>
                <span className={local.barShare}>{percent(bucket.share)}</span>
              </div>
            ))}
          </div>

          <div className={local.groupHead}>
            <h3 className={local.groupTitle}>
              How products behave inside baskets
              <span className={local.groupCount}>{count(data.product_count)} products</span>
            </h3>
          </div>
          <div className={local.tableScroll}>
            <table className={local.table}>
              <thead>
                <tr>
                  <th>ASIN</th>
                  <th className={local.numeric}>Orders containing it</th>
                  <th className={local.numeric}>Avg units in those orders</th>
                  <th className={local.numeric}>Avg products in those orders</th>
                  <th className={local.numeric}>Sold alone</th>
                </tr>
              </thead>
              <tbody>
                {(data.products || []).length === 0 ? (
                  <tr><td className={local.emptyCell} colSpan={5}>No orders in this period.</td></tr>
                ) : data.products.map((row) => (
                  <tr key={row.asin}>
                    <td className={local.asinCell}>{row.asin}</td>
                    <td className={local.numeric}>{count(row.orders_containing)}</td>
                    <td className={local.numeric}>{row.average_basket_units.toFixed(2)}</td>
                    <td className={local.numeric}>{row.average_basket_products.toFixed(2)}</td>
                    <td className={local.numeric}>{percent(row.sold_alone_share, 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {data?.notes?.length > 0 && (
        <ul className={local.noteList}>
          {data.notes.map((note) => (
            <li key={note}><FiInfo className={local.noteIcon} />{note}</li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default ItemsPerOrder;
